import type { Customer } from '../../../api/active/generated/customer';
import type { Equipment } from '../../../api/active/generated/equipment';
import type {
  InventoryReadItem,
  Warehouse,
} from '../../../api/active/generated/inventory';
import type { components } from '../../../api/active/generated/openapi';
import type {
  QuoteFieldDefinition,
  QuoteFieldPoolList,
  QuoteOutputOptions,
} from '../../../api/active/generated/settings';
import type { ActiveRequestInit } from '../../../api/active/roleViewDispatch';
import type { ActiveTransport } from '../../../api/active/transport';
import type {
  CustomerCatalogRecord,
  QuoteFieldCombinationRecord,
  QuoteFieldDefinitionRecord,
  QuoteFieldPoolRecord,
  WarehouseRecord,
} from '../state/types';
import type { LocalCatalogSnapshotDtos } from './localCatalogTransport';
import { createLocalPermissionTransport } from './localPermissionTransport';

type LocalContact = Customer['contacts'][number];
type LocalPool = components['schemas']['ReplacementPool'];
type LocalFieldName = components['schemas']['FieldDisplayName'];
type LocalAnnouncement = components['schemas']['MarketingAnnouncement'];
type LocalQuoteFields = QuoteFieldDefinition[];
type LocalInventory = InventoryReadItem[];
type LocalWarehouse = Warehouse;
type LocalState = {
  customers: Customer[];
  replacementPools: LocalPool[];
  warehouses: LocalWarehouse[];
  inventory: LocalInventory;
  announcement: LocalAnnouncement;
  fieldDisplayNames: LocalFieldName[];
  quoteFields: LocalQuoteFields;
  quoteFieldPool: QuoteFieldPoolList;
  outputOptions: QuoteOutputOptions;
  workspaceSettings: components['schemas']['QuoteWorkspaceSettings'];
  containerEditMode: components['schemas']['QuoteWorkspaceContainerEditMode'];
  workspacePreferences: components['schemas']['QuoteWorkspaceUserPreferences'];
  customerSequence: number;
  poolSequence: number;
};

const STORE_KEY = 'cpq-local-database-demo:v1:module-state';
const DEMO_TIME = '2026-01-01T00:00:00.000Z';
const INVENTORY_OBSERVED_AT = '2026-10-27T00:00:00.000Z';
const WAREHOUSE_CODES = [
  '0053',
  '013',
  '0075',
  '0064',
  '0063',
  '0065',
] as const;
const GROUP_LABELS = {
  additional_info: '补充信息',
  base_price: '基础价格',
  device_fulfillment: '设备与履约',
  plan_package_price: '方案与套餐价格',
  quote_result: '报价结果',
  rental: '租金',
};

const jsonResponse = (value: unknown, status = 200, etag?: string): Response =>
  new Response(status === 204 ? null : JSON.stringify(value), {
    status,
    headers: {
      'Content-Type': 'application/json',
      'X-CPQ-Demo': 'local-only',
      ...(etag ? { ETag: etag } : {}),
    },
  });

function problem(status: number, code: string, detail: string) {
  return jsonResponse(
    { type: 'about:blank', title: detail, status, code, detail },
    status,
  );
}

function pageMeta(url: URL, total: number) {
  const page = Math.max(1, Number(url.searchParams.get('page')) || 1);
  const pageSize = Math.max(1, Number(url.searchParams.get('pageSize')) || 20);
  return { page, pageSize, total, totalPages: Math.ceil(total / pageSize) };
}

function createCustomer(id: string, name: string, index: number): Customer {
  const contact: LocalContact = {
    id: `${id}-contact`,
    name: `演示联系人${index}`,
    phone: `1380000000${Math.max(0, index - 1)}`,
    phoneAvailability: 'masked',
    primary: true,
    status: 'active',
  };
  return {
    id,
    customerNo: `LOCAL-DEMO-${String(index).padStart(3, '0')}`,
    name,
    status: 'active',
    sourceType: 'manual',
    sourceId: null,
    sourceSystem: null,
    departmentId: 'offline-demo-department',
    ownerUserIds: ['offline-demo-user'],
    primaryOwnerUserId: 'offline-demo-user',
    primaryOwnerName: '本地演示用户',
    customFields: [],
    contacts: [contact],
    authorization: {
      allowedActions: ['customer.read', 'customer.manage'],
      fieldCapabilities: [],
      permissionVersion: 'local-demo',
      stateActions: ['activate', 'deactivate'],
    },
    rowVersion: '1',
    createdAt: DEMO_TIME,
    updatedAt: DEMO_TIME,
  };
}

function createWarehouses(): LocalWarehouse[] {
  return WAREHOUSE_CODES.map((warehouseCode, index) => ({
    warehouseCode,
    warehouseId: String(99001 + index),
    name: `本地演示仓库 ${warehouseCode}`,
  }));
}

function createInventory(snapshot?: LocalCatalogSnapshotDtos): LocalInventory {
  return (snapshot?.equipmentDtos ?? []).map((equipment: Equipment, index) => {
    // Rehearsal values deliberately exercise UNKNOWN, zero, low, and sufficient
    // stock states; they are not copied from or written to the database.
    const scenario = index % 5;
    const total =
      scenario === 0
        ? null
        : scenario === 1
          ? 0
          : scenario === 2
            ? 2
            : scenario === 3
              ? 36
              : 12;
    const warehouses = WAREHOUSE_CODES.map((warehouseCode, warehouseIndex) => {
      const unknown =
        total === null || (scenario === 4 && warehouseIndex === 5);
      const quantity =
        total === null
          ? null
          : Math.floor(total / 6) + (warehouseIndex < total % 6 ? 1 : 0);
      return {
        warehouseCode,
        displayQuantity: unknown ? null : quantity,
        observedAt: unknown ? null : INVENTORY_OBSERVED_AT,
        readStatus: unknown ? ('UNKNOWN' as const) : ('KNOWN' as const),
      };
    });
    const knownTotal = warehouses
      .filter((item) => item.readStatus === 'KNOWN')
      .reduce((sum, item) => sum + (item.displayQuantity ?? 0), 0);
    const known = warehouses.some((item) => item.readStatus === 'KNOWN');
    return {
      deviceId: equipment.id,
      deviceRevisionId: equipment.currentRevisionId,
      deviceName: equipment.name,
      deviceSku: equipment.skuCode ?? undefined,
      displayReceipt: `LOCAL-DEMO-INVENTORY:${equipment.id}`,
      observedAt: known ? INVENTORY_OBSERVED_AT : null,
      readStatus: known ? 'KNOWN' : 'UNKNOWN',
      totalDisplayQuantity: known ? knownTotal : null,
      warehouses,
    };
  });
}

function makeField(
  id: string,
  resourceType: LocalFieldName['resourceType'],
  groupKey: string,
  fieldKey: string,
  labelZh: string,
  sortOrder: number,
  valueType: QuoteFieldDefinition['valueType'] = 'text',
): QuoteFieldDefinition {
  const options =
    valueType === 'single_select' || valueType === 'multi_select'
      ? [
          {
            value: 'standard',
            displayLabel: '标准',
            enabled: true,
            sortOrder: 1,
          },
          {
            value: 'custom',
            displayLabel: '定制',
            enabled: true,
            sortOrder: 2,
          },
        ]
      : null;
  return {
    id,
    resourceType,
    fieldKey,
    labelZh,
    groupKey,
    groupLabelZh: resourceType === 'quote_item' ? '报价明细' : '基本信息',
    noteZh: '本地演示字段',
    valueType,
    width: 'normal',
    securityClass: 'ordinary',
    enabled: true,
    defaultVisible: true,
    sourceKind: 'manual',
    systemLocked: false,
    sortOrder,
    definitionVersion: 1,
    options,
    optionsVersion: options ? '1' : null,
    rowVersion: '1',
    createWritable: true,
  } as QuoteFieldDefinition;
}

function createDefaultState(snapshot?: LocalCatalogSnapshotDtos): LocalState {
  const quoteFields: LocalQuoteFields = [
    makeField(
      'local-demo-field-quote-1',
      'quote',
      'project',
      'project_name',
      '项目名称',
      1,
    ),
    makeField(
      'local-demo-field-item-1',
      'quote_item',
      'device',
      'device_color',
      '设备颜色',
      2,
      'single_select',
    ),
    makeField(
      'local-demo-field-item-2',
      'quote_item',
      'device',
      'delivery_note',
      '交付说明',
      3,
    ),
    makeField(
      'local-demo-field-customer-1',
      'customer',
      'customer',
      'customer_level',
      '客户等级',
      4,
      'single_select',
    ),
  ];
  const fieldDisplayNames: LocalFieldName[] = [
    ['quote', 'base', 'quoteNo', '报价编号'],
    ['quote', 'base', 'projectName', '项目名称'],
    ['quote_item', 'device', 'name', '设备名称'],
    ['quote_item', 'device', 'sku', '设备SKU'],
    ['quote_item', 'device', 'category', '设备分类'],
    ['quote_item', 'pricing', 'rentalPrice', '月租金'],
    ['customer', 'base', 'name', '客户名称'],
  ].map(([resourceType, groupKey, fieldKey, label]) => ({
    id: '0',
    resourceType: resourceType as LocalFieldName['resourceType'],
    groupKey: String(groupKey),
    fieldKey: String(fieldKey),
    defaultLabelZh: String(label),
    labelZh: String(label),
    rowVersion: '0',
  }));
  const poolBindings: QuoteFieldPoolList['items'] = quoteFields
    .filter((item) => item.resourceType === 'quote_item')
    .map((item, index) => ({
      id: `local-demo-binding-${index + 1}`,
      definitionId: item.id,
      definitionVersion: String(item.definitionVersion),
      fieldKey: item.fieldKey,
      displayLabel: item.labelZh,
      placement: 'quote-line',
      snapshotPolicy: 'freeze-on-select',
      enabled: true,
      defaultVisible: true,
      mandatory: false,
      sourceKind: 'manual',
      systemLocked: false,
      deletedAt: null,
      deletedBy: null,
      rowVersion: '1',
      sortOrder: index + 1,
    }));
  const combinationIds = [
    'share-combination-customer-standard',
    'FIELDS-DEFAULT-TABLE',
    'FIELDS-THREE-STORE-FLIP',
    'FIELDS-EXPORT-THREE-STORE',
    'FIELDS-EXPORT-TWO-STORE',
    'FIELDS-PURCHASE-WORKSHEET',
    'FIELDS-EXPORT-PURCHASE-TAX',
    'FIELDS-ATTRIBUTE-TABLE',
  ] as const;
  const styleIds = [
    'STYLE-DEFAULT',
    'STYLE-DEFAULT-TABLE',
    'STYLE-THREE-STORE-FLIP',
    'STYLE-THREE-STORE',
    'STYLE-TWO-STORE',
    'STYLE-PURCHASE-WORKSHEET',
    'STYLE-PURCHASE-TAX',
    'STYLE-ATTRIBUTE-TABLE',
  ] as const;
  const outputOptions: QuoteOutputOptions = {
    aggregateResourceVersion: '1',
    fieldCombinations: combinationIds.map((id, index) => ({
      id,
      name:
        [
          '客户标准',
          '默认表格',
          '三店翻页',
          '三店报价',
          '两店报价',
          '采购工作表',
          '采购含税',
          '设备属性',
        ][index] ?? '本地演示组合',
      businessContent:
        index === 5 || index === 6
          ? 'purchase'
          : index === 7
            ? 'attributes'
            : index === 2
              ? 'rental-three-store'
              : index === 3
                ? 'rental-three-store'
                : index === 4
                  ? 'rental-two-store'
                  : 'default',
      columns: [
        { key: 'name', label: '设备名称', valueType: 'text', width: 220 },
        { key: 'quantity', label: '数量', valueType: 'integer', width: 80 },
        { key: 'price', label: '单价', valueType: 'money', width: 120 },
      ],
      enabled: true,
      version: '1',
    })),
    styles: styleIds.map((id, index) => ({
      id,
      name:
        [
          '默认样式',
          '默认表格',
          '三店翻页',
          '三店报价',
          '两店报价',
          '采购工作表',
          '采购含税',
          '设备属性',
        ][index] ?? '本地演示样式',
      enabled: true,
      themeColor: '#1677ff',
      titleBackground: '#ffffff',
      headerBackground: '#f5f7fa',
      headerTextColor: '#1f2937',
      version: '1',
    })),
  };
  return {
    customers: [
      createCustomer('local-demo-customer-1', '本地演示客户·方案测试', 1),
      createCustomer('local-demo-customer-2', '本地演示客户·设备测试', 2),
    ],
    replacementPools: createReplacementPools(snapshot),
    warehouses: createWarehouses(),
    inventory: createInventory(snapshot),
    announcement: {
      resourceVersion: '1',
      headlineLines: [
        { text: '本地演示数据', color: '#595959' },
        { text: '改动仅保存在当前浏览器', color: '#595959' },
      ],
      categories: snapshot?.equipmentDtos?.length
        ? [
            {
              name: '演示推荐设备',
              enabled: true,
              deviceIds: snapshot.equipmentDtos
                .filter((item) => item.status === 'active')
                .slice(0, 4)
                .map((item) => item.id),
            },
            {
              name: '演示备选设备',
              enabled: false,
              deviceIds: snapshot.equipmentDtos
                .filter((item) => item.status === 'active')
                .slice(4, 8)
                .map((item) => item.id),
            },
          ]
        : [],
      deviceLabels: Object.fromEntries(
        (snapshot?.equipmentDtos ?? []).map((item) => [
          item.id,
          { name: item.name, sku: item.skuCode ?? undefined },
        ]),
      ),
    },
    fieldDisplayNames,
    quoteFields,
    quoteFieldPool: { aggregateResourceVersion: '1', items: poolBindings },
    outputOptions,
    workspaceSettings: {
      groupLabels: GROUP_LABELS,
      resourceVersion: '0',
      enabledAutoMatch: false,
    },
    containerEditMode: { enabled: false, resourceVersion: '0' },
    workspacePreferences: {
      selectedEquipmentPriceFields: [],
      resourceVersion: '0',
    },
    customerSequence: 3,
    poolSequence: 3,
  };
}

function createReplacementPools(
  snapshot?: LocalCatalogSnapshotDtos,
): LocalPool[] {
  const devices = (snapshot?.equipmentDtos ?? []).filter(
    (item) => item.status === 'active',
  );
  const groups = [devices.slice(0, 3), devices.slice(3, 6)];
  return groups.map((members, index) => ({
    id: `local-demo-pool-${index + 1}`,
    code: `LOCAL-POOL-${index + 1}`,
    name: index === 0 ? '本地演示平替组·办公设备' : '本地演示平替组·备选设备',
    status: index === 0 ? 'active' : 'inactive',
    currentRevisionId: `local-demo-pool-revision-${index + 1}`,
    resourceVersion: '1',
    revisionNo: '1',
    rowVersion: '1',
    warning: members.length ? null : 'NO_ACTIVE_MEMBER',
    boundPackageRelationCount: 0,
    members: members.map((equipment, memberIndex) => ({
      memberId: `${index + 1}-${memberIndex + 1}`,
      deviceId: equipment.id,
      deviceRevisionId: equipment.currentRevisionId,
      name: equipment.name,
      skuCode: equipment.skuCode ?? undefined,
      isEffectivePromoted: memberIndex === 0,
      status: 'active',
      sortOrder: memberIndex + 1,
    })),
    createdAt: DEMO_TIME,
    updatedAt: DEMO_TIME,
  }));
}

function readState(snapshot?: LocalCatalogSnapshotDtos): LocalState {
  try {
    const raw = window.localStorage.getItem(STORE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as LocalState;
      if (
        parsed &&
        Array.isArray(parsed.customers) &&
        Array.isArray(parsed.inventory)
      ) {
        for (const [id, phone] of [
          ['local-demo-customer-1', '13800000000'],
          ['local-demo-customer-2', '13800000001'],
        ]) {
          const seedCustomer = parsed.customers.find((item) => item.id === id);
          const seedContact = seedCustomer?.contacts.find(
            (item) => item.primary,
          );
          if (seedContact?.phone === '00000000000') seedContact.phone = phone;
        }
        if (!parsed.inventory.length && snapshot?.equipmentDtos.length)
          parsed.inventory = createInventory(snapshot);
        if (!parsed.warehouses?.length) parsed.warehouses = createWarehouses();
        return parsed;
      }
    }
  } catch {
    // A malformed local rehearsal snapshot is replaced only in the demo key.
  }
  const seeded = createDefaultState(snapshot);
  writeState(seeded);
  return seeded;
}

function writeState(state: LocalState) {
  window.localStorage.setItem(STORE_KEY, JSON.stringify(state));
  window.dispatchEvent(new CustomEvent('cpq-local-demo-modules-changed'));
}

function bodyOf(init: ActiveRequestInit): Record<string, unknown> {
  if (typeof init.body !== 'string') return {};
  try {
    const parsed: unknown = JSON.parse(init.body);
    return parsed !== null &&
      typeof parsed === 'object' &&
      !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : {};
  } catch {
    return {};
  }
}

function currentVersion(init: ActiveRequestInit) {
  return init.headers instanceof Headers
    ? init.headers.get('If-Match')?.replaceAll('"', '')
    : (init.headers as Record<string, string> | undefined)?.[
        'If-Match'
      ]?.replaceAll('"', '');
}

function normalized(value: string) {
  return value.trim().toLocaleLowerCase();
}

function cpqFieldKey(id: string) {
  let hash = 2166136261;
  for (const char of id) {
    hash ^= char.charCodeAt(0);
    hash = Math.imul(hash, 16777619);
  }
  return `field.custom.new_${hash >>> 0 || 1}`;
}

function workbenchField(
  field: QuoteFieldDefinition,
  placement?: QuoteFieldPoolList['items'][number]['placement'],
): QuoteFieldDefinitionRecord | null {
  if (field.resourceType === 'quote' || field.valueType === 'image')
    return null;
  const owner = field.resourceType === 'customer' ? 'customer' : 'quote-line';
  const key = cpqFieldKey(field.id);
  const typeMap = {
    text: 'text',
    number: 'number',
    money: 'money',
    percent: 'percent',
    code: 'code',
    date: 'date',
    boolean: 'boolean',
    single_select: 'single-select',
    multi_select: 'multi-select',
  } as const;
  const active = field.enabled;
  return {
    key,
    label: field.labelZh,
    type: typeMap[field.valueType as keyof typeof typeMap],
    width: field.width,
    note: field.noteZh ?? undefined,
    groupKey: `business:${owner}`,
    groupLabel: field.groupLabelZh,
    sortOrder: field.sortOrder,
    enabled: active,
    defaultVisible: active && field.defaultVisible,
    sourceKind: 'manual',
    systemLocked: false,
    options: field.options?.map((option) => ({
      value: option.value,
      label: option.displayLabel,
    })),
    ...(owner === 'quote-line'
      ? {
          quotePlacement:
            placement === 'package-summary'
              ? ('package-summary' as const)
              : ('quote-line' as const),
        }
      : {}),
    governance: {
      fieldKey: key,
      kind: 'custom',
      classification: active ? field.securityClass : 'unclassified',
      required: false,
      definitionVersion: `definition-v${field.definitionVersion}`,
      lockedAttributes: ['fieldKey', 'kind'],
    },
  };
}

function projection(state: LocalState) {
  const customers: CustomerCatalogRecord[] = state.customers.map((item) => {
    const primary =
      item.contacts.find((contact) => contact.primary) ?? item.contacts[0];
    return {
      id: item.id,
      customerNo: item.customerNo,
      name: item.name,
      contact: primary?.name ?? '',
      phone: primary?.phone ?? '',
      manager: item.primaryOwnerName ?? '本地演示用户',
      managerUserId: item.primaryOwnerUserId,
      status: item.status === 'active' ? 'active' : 'inactive',
      customFieldValues: Object.fromEntries(
        item.customFields.map((field) => [
          cpqFieldKey(field.definitionId),
          field.value as string | number | boolean | string[],
        ]),
      ),
    };
  });
  const warehouses: WarehouseRecord[] = state.warehouses.map((item) => ({
    key: item.warehouseCode,
    name: item.name,
  }));
  const inventoryByDeviceId = Object.fromEntries(
    state.inventory.map((item) => [
      item.deviceId,
      Object.fromEntries(
        item.warehouses
          .filter((warehouse) => warehouse.readStatus === 'KNOWN')
          .map((warehouse) => [
            warehouse.warehouseCode,
            warehouse.displayQuantity,
          ]),
      ),
    ]),
  );
  const workbenchFieldDefinitions = state.quoteFields
    .map((field) => {
      const placement = state.quoteFieldPool.items.find(
        (binding) => binding.definitionId === field.id,
      )?.placement;
      return workbenchField(field, placement);
    })
    .filter((field): field is QuoteFieldDefinitionRecord => field !== null);
  const workbenchKeyById = new Map(
    state.quoteFields.map((field) => [field.id, cpqFieldKey(field.id)]),
  );
  const workbenchFieldPool: QuoteFieldPoolRecord[] =
    state.quoteFieldPool.items.flatMap((binding) => {
      const field = state.quoteFields.find(
        (item) => item.id === binding.definitionId,
      );
      const key = workbenchKeyById.get(binding.definitionId);
      if (
        field?.resourceType !== 'quote_item' ||
        !key ||
        binding.placement === 'quote-header'
      )
        return [];
      return [
        {
          id: `local-demo-cpq-binding-${binding.id}`,
          fieldKey: key,
          displayLabel: binding.displayLabel,
          placement: binding.placement,
          snapshotPolicy: 'freeze-on-select' as const,
          enabled: binding.enabled && field.enabled,
          mandatory: binding.mandatory,
          defaultVisible: binding.defaultVisible,
          sortOrder: binding.sortOrder,
        },
      ];
    });
  const combinationFieldKeys = workbenchFieldPool
    .filter((item) => item.enabled)
    .map((item) => item.fieldKey);
  const workbenchFieldCombinations: QuoteFieldCombinationRecord[] =
    combinationFieldKeys.length
      ? [
          {
            id: 'local-demo-combination-custom-field-pool',
            name: '本地演示自定义字段集合',
            fieldKeys: combinationFieldKeys,
            scope: '全公司',
            isDefault: false,
            enabled: true,
            sortOrder: 10000,
            systemLocked: false,
          },
        ]
      : [];
  return {
    customers,
    warehouses,
    inventoryByDeviceId,
    inventoryReadItems: structuredClone(state.inventory),
    replacementPools: structuredClone(state.replacementPools),
    fieldDefinitions: structuredClone(state.quoteFields),
    fieldDisplayNames: structuredClone(state.fieldDisplayNames),
    quoteFieldPool: structuredClone(state.quoteFieldPool),
    quoteOutputOptions: structuredClone(state.outputOptions),
    workspaceSettings: structuredClone(state.workspaceSettings),
    workspacePreferences: structuredClone(state.workspacePreferences),
    workbenchFieldDefinitions,
    workbenchFieldPool,
    workbenchFieldCombinations,
  };
}

/** Current local rehearsal modules projected for the Disposable quote state. */
export function getLocalModuleCatalogProjection(
  snapshot?: LocalCatalogSnapshotDtos,
) {
  return projection(readState(snapshot));
}

function moduleResponse(
  pathname: string,
  url: URL,
  method: string,
  init: ActiveRequestInit,
  snapshot?: LocalCatalogSnapshotDtos,
): Response | undefined {
  const customerMatch = /^\/customers\/([^/]+)$/.exec(pathname);
  const contactMatch =
    /^\/customers\/([^/]+)\/contacts(?:\/([^/:]+)(:set-primary)?)?$/.exec(
      pathname,
    );
  const poolMatch = /^\/replacement-pools\/([^/]+)$/.exec(pathname);
  const fieldMatch = /^\/quote-fields\/([^/]+)$/.exec(pathname);
  const displayNameMatch =
    /^\/field-display-names\/([^/]+)\/([^/]+)\/([^/]+)$/.exec(pathname);
  const moduleRequest =
    (pathname === '/customers' && ['GET', 'POST'].includes(method)) ||
    (customerMatch !== null && ['GET', 'PATCH'].includes(method)) ||
    (contactMatch !== null && ['POST', 'PATCH'].includes(method)) ||
    (pathname === '/customers:check-duplicates' && method === 'POST') ||
    (pathname === '/replacement-pools' && ['GET', 'POST'].includes(method)) ||
    (poolMatch !== null && ['PATCH', 'DELETE'].includes(method)) ||
    ((pathname === '/warehouses' || pathname === '/inventory') &&
      method === 'GET') ||
    (pathname === '/marketing-announcement' &&
      ['GET', 'PUT'].includes(method)) ||
    (pathname === '/field-display-names' && method === 'GET') ||
    (displayNameMatch !== null && method === 'PUT') ||
    (pathname === '/quote-fields' && ['GET', 'POST'].includes(method)) ||
    (fieldMatch !== null && method === 'PUT') ||
    (pathname === '/quote-field-pool' && ['GET', 'PUT'].includes(method)) ||
    (pathname === '/quote-output-options' && ['GET', 'PUT'].includes(method)) ||
    (pathname === '/quote-workspace-settings' &&
      ['GET', 'PUT'].includes(method)) ||
    (pathname === '/quote-workspace-container-edit-mode' &&
      ['GET', 'PUT'].includes(method)) ||
    (pathname === '/quote-workspace-preferences' &&
      ['GET', 'PUT'].includes(method));
  if (!moduleRequest) return undefined;

  const state = readState(snapshot);
  const body = bodyOf(init);
  const bump = (current: string | number) => String(Number(current) + 1);
  const write = (response: unknown, status = 200, etag?: string) => {
    writeState(state);
    return jsonResponse(response, status, etag);
  };

  if (pathname === '/customers' && method === 'GET') {
    const search = normalized(url.searchParams.get('search') ?? '');
    const filtered = state.customers.filter((customer) =>
      normalized(customer.name).includes(search),
    );
    const meta = pageMeta(url, filtered.length);
    return jsonResponse({
      items: filtered.slice(
        (meta.page - 1) * meta.pageSize,
        meta.page * meta.pageSize,
      ),
      meta,
      createAuthorization: state.customers[0]?.authorization,
    });
  }
  if (pathname === '/customers:check-duplicates' && method === 'POST') {
    const name = normalized(String(body.name ?? ''));
    const phone = normalized(String(body.phone ?? ''));
    return jsonResponse({
      items: state.customers
        .filter((customer) => {
          const contact =
            customer.contacts.find((item) => item.primary) ??
            customer.contacts[0];
          return (
            (name && normalized(customer.name) === name) ||
            (phone && normalized(contact?.phone ?? '') === phone)
          );
        })
        .map((customer) => ({
          customerId: customer.id,
          customerNo: customer.customerNo,
          name: customer.name,
          contactPhone: '000****0000',
          contactPhoneAvailability: 'masked',
          matchTypes: [
            normalized(customer.name) === name
              ? 'normalized_name'
              : 'normalized_phone',
          ],
        })),
    });
  }
  if (pathname === '/customers' && method === 'POST') {
    const primary =
      body.primaryContact && typeof body.primaryContact === 'object'
        ? (body.primaryContact as Record<string, unknown>)
        : {};
    const sequence = state.customerSequence++;
    const id = `local-demo-customer-${crypto.randomUUID()}`;
    const customer = createCustomer(
      id,
      String(body.name ?? `本地演示客户${sequence}`),
      sequence,
    );
    customer.customerNo = `LOCAL-DEMO-${String(sequence).padStart(3, '0')}`;
    customer.departmentId = String(
      body.departmentId ?? 'offline-demo-department',
    );
    customer.ownerUserIds = Array.isArray(body.ownerUserIds)
      ? body.ownerUserIds.map(String)
      : ['offline-demo-user'];
    customer.primaryOwnerUserId = String(
      body.primaryOwnerUserId ??
        customer.ownerUserIds[0] ??
        'offline-demo-user',
    );
    customer.status = body.status === 'inactive' ? 'inactive' : 'active';
    customer.customFields = Array.isArray(body.customFields)
      ? (body.customFields as Customer['customFields'])
      : [];
    if (typeof primary.name === 'string')
      customer.contacts = [
        {
          id: `${id}-contact-1`,
          name: primary.name,
          phone: String(
            primary.phone ?? `1380000000${Math.max(0, sequence - 1)}`,
          ),
          phoneAvailability: 'full',
          primary: true,
          status: 'active',
        },
      ];
    state.customers.push(customer);
    return write(customer, 201, `"${customer.rowVersion}"`);
  }
  if (customerMatch && method === 'GET') {
    const customer = state.customers.find(
      (item) => item.id === decodeURIComponent(customerMatch[1]),
    );
    return customer
      ? jsonResponse(customer, 200, `"${customer.rowVersion}"`)
      : problem(404, 'CUSTOMER_NOT_FOUND', '没有找到这条本地演示客户');
  }
  if (customerMatch && method === 'PATCH') {
    const customer = state.customers.find(
      (item) => item.id === decodeURIComponent(customerMatch[1]),
    );
    if (!customer)
      return problem(404, 'CUSTOMER_NOT_FOUND', '没有找到这条本地演示客户');
    if (currentVersion(init) && currentVersion(init) !== customer.rowVersion)
      return problem(
        409,
        'RESOURCE_VERSION_CONFLICT',
        '客户已在本地演示中更新，请重新读取',
      );
    if (typeof body.name === 'string') customer.name = body.name;
    if (body.status === 'active' || body.status === 'inactive')
      customer.status = body.status;
    if (Array.isArray(body.ownerUserIds))
      customer.ownerUserIds = body.ownerUserIds.map(String);
    if (typeof body.primaryOwnerUserId === 'string')
      customer.primaryOwnerUserId = body.primaryOwnerUserId;
    if (typeof body.departmentId === 'string')
      customer.departmentId = body.departmentId;
    if (Array.isArray(body.customFields))
      customer.customFields = body.customFields as Customer['customFields'];
    customer.rowVersion = bump(customer.rowVersion);
    customer.updatedAt = DEMO_TIME;
    return write(customer, 200, `"${customer.rowVersion}"`);
  }
  if (contactMatch && method === 'POST' && !contactMatch[2]) {
    const customer = state.customers.find(
      (item) => item.id === decodeURIComponent(contactMatch[1]),
    );
    if (!customer)
      return problem(404, 'CUSTOMER_NOT_FOUND', '没有找到这条本地演示客户');
    if (currentVersion(init) && currentVersion(init) !== customer.rowVersion)
      return problem(
        409,
        'RESOURCE_VERSION_CONFLICT',
        '客户已更新，请重新读取',
      );
    const contact: LocalContact = {
      id: `${customer.id}-contact-${crypto.randomUUID()}`,
      name: String(body.name ?? '演示联系人'),
      phone: String(body.phone ?? '13800000000'),
      phoneAvailability: 'full',
      primary: customer.contacts.length === 0,
      status: 'active',
    };
    customer.contacts.push(contact);
    customer.rowVersion = bump(customer.rowVersion);
    return write(customer, 201, `"${customer.rowVersion}"`);
  }
  if (contactMatch?.[2] && method === 'PATCH' && !contactMatch[3]) {
    const customer = state.customers.find(
      (item) => item.id === decodeURIComponent(contactMatch[1]),
    );
    const contact = customer?.contacts.find(
      (item) => item.id === decodeURIComponent(contactMatch[2]),
    );
    if (!customer || !contact)
      return problem(404, 'CONTACT_NOT_FOUND', '没有找到这条本地演示联系人');
    if (currentVersion(init) && currentVersion(init) !== customer.rowVersion)
      return problem(
        409,
        'RESOURCE_VERSION_CONFLICT',
        '客户已更新，请重新读取',
      );
    if (typeof body.name === 'string') contact.name = body.name;
    if (typeof body.phone === 'string') contact.phone = body.phone;
    if (body.status === 'active' || body.status === 'inactive')
      contact.status = body.status;
    customer.rowVersion = bump(customer.rowVersion);
    return write(customer, 200, `"${customer.rowVersion}"`);
  }
  if (
    contactMatch?.[2] &&
    contactMatch[3] === ':set-primary' &&
    method === 'POST'
  ) {
    const customer = state.customers.find(
      (item) => item.id === decodeURIComponent(contactMatch[1]),
    );
    const contact = customer?.contacts.find(
      (item) => item.id === decodeURIComponent(contactMatch[2]),
    );
    if (!customer || !contact)
      return problem(404, 'CONTACT_NOT_FOUND', '没有找到这条本地演示联系人');
    if (currentVersion(init) && currentVersion(init) !== customer.rowVersion)
      return problem(
        409,
        'RESOURCE_VERSION_CONFLICT',
        '客户已更新，请重新读取',
      );
    customer.contacts.forEach((item) => {
      item.primary = item.id === contact.id;
    });
    customer.rowVersion = bump(customer.rowVersion);
    return write(customer, 200, `"${customer.rowVersion}"`);
  }

  if (pathname === '/replacement-pools' && method === 'GET') {
    const search = normalized(url.searchParams.get('search') ?? '');
    const filtered = state.replacementPools.filter((item) =>
      normalized(item.name ?? '').includes(search),
    );
    const meta = pageMeta(url, filtered.length);
    return jsonResponse({
      items: filtered.slice(
        (meta.page - 1) * meta.pageSize,
        meta.page * meta.pageSize,
      ),
      meta,
    });
  }
  if (pathname === '/replacement-pools' && method === 'POST') {
    const id = `local-demo-pool-${crypto.randomUUID()}`;
    const requestMembers = Array.isArray(body.members)
      ? (body.members as Array<Record<string, unknown>>)
      : [];
    const pool: LocalPool = {
      id,
      code: `LOCAL-POOL-${state.poolSequence++}`,
      name: String(body.name ?? '本地演示平替组'),
      status: body.status === 'inactive' ? 'inactive' : 'active',
      currentRevisionId: `${id}-revision-1`,
      resourceVersion: '1',
      revisionNo: '1',
      rowVersion: '1',
      warning: requestMembers.length ? null : 'NO_ACTIVE_MEMBER',
      boundPackageRelationCount: 0,
      members: requestMembers.map((member, index) => {
        const device = snapshot?.equipmentDtos.find(
          (item) => item.id === String(member.deviceId),
        );
        return {
          memberId: `${id}-member-${index + 1}`,
          deviceId: String(member.deviceId),
          deviceRevisionId: device?.currentRevisionId ?? 'local-demo-revision',
          name: device?.name ?? '本地演示设备',
          skuCode: device?.skuCode ?? undefined,
          isEffectivePromoted: index === 0,
          status: 'active',
          sortOrder: index + 1,
        };
      }),
      createdAt: DEMO_TIME,
      updatedAt: DEMO_TIME,
    };
    state.replacementPools.push(pool);
    return write(pool, 201, `"${pool.rowVersion}"`);
  }
  if (poolMatch && method === 'PATCH') {
    const pool = state.replacementPools.find(
      (item) => item.id === decodeURIComponent(poolMatch[1]),
    );
    if (!pool)
      return problem(
        404,
        'REPLACEMENT_POOL_NOT_FOUND',
        '没有找到这条本地演示平替组',
      );
    if (currentVersion(init) && currentVersion(init) !== pool.rowVersion)
      return problem(
        409,
        'RESOURCE_VERSION_CONFLICT',
        '平替组已更新，请重新读取',
      );
    const requestMembers = Array.isArray(body.members)
      ? (body.members as Array<Record<string, unknown>>)
      : [];
    pool.name = typeof body.name === 'string' ? body.name : pool.name;
    pool.status = body.status === 'inactive' ? 'inactive' : 'active';
    pool.members = requestMembers.map((member, index) => {
      const device = snapshot?.equipmentDtos.find(
        (item) => item.id === String(member.deviceId),
      );
      return {
        memberId: `${pool.id}-member-${index + 1}`,
        deviceId: String(member.deviceId),
        deviceRevisionId: device?.currentRevisionId ?? 'local-demo-revision',
        name: device?.name ?? '本地演示设备',
        skuCode: device?.skuCode ?? undefined,
        isEffectivePromoted: index === 0,
        status: 'active',
        sortOrder: index + 1,
      };
    });
    pool.warning = pool.members.length ? null : 'NO_ACTIVE_MEMBER';
    pool.rowVersion = bump(pool.rowVersion);
    pool.resourceVersion = bump(pool.resourceVersion);
    pool.revisionNo = bump(pool.revisionNo);
    pool.currentRevisionId = `${pool.id}-revision-${pool.revisionNo}`;
    pool.updatedAt = DEMO_TIME;
    return write(pool, 200, `"${pool.rowVersion}"`);
  }
  if (poolMatch && method === 'DELETE') {
    const index = state.replacementPools.findIndex(
      (item) => item.id === decodeURIComponent(poolMatch[1]),
    );
    if (index < 0)
      return problem(
        404,
        'REPLACEMENT_POOL_NOT_FOUND',
        '没有找到这条本地演示平替组',
      );
    const pool = state.replacementPools[index];
    if (currentVersion(init) && currentVersion(init) !== pool.rowVersion)
      return problem(
        409,
        'RESOURCE_VERSION_CONFLICT',
        '平替组已更新，请重新读取',
      );
    state.replacementPools.splice(index, 1);
    return write(undefined, 204);
  }

  if (pathname === '/warehouses' && method === 'GET')
    return jsonResponse({ items: state.warehouses });
  if (pathname === '/inventory' && method === 'GET') {
    const ids = [
      ...url.searchParams.getAll('deviceId'),
      ...url.searchParams.getAll('deviceIds'),
    ];
    const search = normalized(url.searchParams.get('search') ?? '');
    const warehouseCode = url.searchParams.get('warehouseCode');
    const readStatus = url.searchParams.get('readStatus');
    const min = url.searchParams.get('displayQuantityMin');
    const max = url.searchParams.get('displayQuantityMax');
    const filtered = state.inventory.filter((item) => {
      if (ids.length && !ids.includes(item.deviceId)) return false;
      if (
        search &&
        !normalized(
          `${item.deviceName ?? ''} ${item.deviceSku ?? ''}`,
        ).includes(search)
      )
        return false;
      const quantity = warehouseCode
        ? item.warehouses.find(
            (warehouse) => warehouse.warehouseCode === warehouseCode,
          )
        : undefined;
      const status = quantity?.readStatus ?? item.readStatus;
      const displayed = warehouseCode
        ? quantity?.displayQuantity
        : item.totalDisplayQuantity;
      if (readStatus && status !== readStatus) return false;
      if (
        min !== null &&
        (displayed === null ||
          displayed === undefined ||
          displayed < Number(min))
      )
        return false;
      if (
        max !== null &&
        (displayed === null ||
          displayed === undefined ||
          displayed > Number(max))
      )
        return false;
      return true;
    });
    const meta = pageMeta(url, filtered.length);
    return jsonResponse({
      items: filtered.slice(
        (meta.page - 1) * meta.pageSize,
        meta.page * meta.pageSize,
      ),
      meta,
    });
  }

  if (pathname === '/marketing-announcement' && method === 'GET')
    return jsonResponse(
      state.announcement,
      200,
      `"${state.announcement.resourceVersion}"`,
    );
  if (pathname === '/marketing-announcement' && method === 'PUT') {
    state.announcement = {
      resourceVersion: String(Number(state.announcement.resourceVersion) + 1),
      headlineLines: Array.isArray(body.headlineLines)
        ? (body.headlineLines as LocalAnnouncement['headlineLines'])
        : state.announcement.headlineLines,
      categories: Array.isArray(body.categories)
        ? (body.categories as LocalAnnouncement['categories']).map(
            ({ name, enabled, deviceIds }) => ({ name, enabled, deviceIds }),
          )
        : state.announcement.categories,
      deviceLabels: state.announcement.deviceLabels,
    };
    return write(
      state.announcement,
      200,
      `"${state.announcement.resourceVersion}"`,
    );
  }

  if (pathname === '/field-display-names' && method === 'GET')
    return jsonResponse({ items: state.fieldDisplayNames });
  if (displayNameMatch && method === 'PUT') {
    const item = state.fieldDisplayNames.find(
      (field) =>
        field.resourceType === displayNameMatch[1] &&
        field.groupKey === decodeURIComponent(displayNameMatch[2]) &&
        field.fieldKey === decodeURIComponent(displayNameMatch[3]),
    );
    if (!item)
      return problem(
        404,
        'FIELD_DISPLAY_NAME_NOT_FOUND',
        '没有找到该演示字段名称',
      );
    if (currentVersion(init) && currentVersion(init) !== item.rowVersion)
      return problem(
        409,
        'RESOURCE_VERSION_CONFLICT',
        '字段名称已更新，请重新读取',
      );
    item.labelZh = String(body.labelZh ?? item.labelZh);
    item.id =
      item.id === '0'
        ? `local-demo-field-name-${crypto.randomUUID()}`
        : item.id;
    item.rowVersion = item.rowVersion === '0' ? '1' : bump(item.rowVersion);
    return write(item, 200, `"${item.rowVersion}"`);
  }
  if (pathname === '/quote-fields' && method === 'GET')
    return jsonResponse({ items: state.quoteFields });
  if (pathname === '/quote-fields' && method === 'POST') {
    const id = `local-demo-field-${crypto.randomUUID()}`;
    const valueType = String(
      body.valueType ?? 'text',
    ) as QuoteFieldDefinition['valueType'];
    const field = makeField(
      id,
      String(
        body.resourceType ?? 'quote_item',
      ) as LocalFieldName['resourceType'],
      String(body.groupKey ?? 'demo'),
      String(body.fieldKey ?? id),
      String(body.labelZh ?? '本地演示字段'),
      Number(body.sortOrder ?? state.quoteFields.length + 1),
      valueType,
    );
    Object.assign(field, body, {
      id,
      rowVersion: '1',
      definitionVersion: 1,
      sourceKind: 'manual',
      systemLocked: false,
      createWritable: true,
    });
    state.quoteFields.push(field);
    state.fieldDisplayNames.push({
      id: '0',
      resourceType: field.resourceType,
      groupKey: field.groupKey,
      fieldKey: field.fieldKey,
      defaultLabelZh: field.labelZh,
      labelZh: field.labelZh,
      rowVersion: '0',
    });
    return write(field, 201, `"${field.rowVersion}"`);
  }
  if (fieldMatch && method === 'PUT') {
    const field = state.quoteFields.find(
      (item) => item.id === decodeURIComponent(fieldMatch[1]),
    );
    if (!field)
      return problem(404, 'QUOTE_FIELD_NOT_FOUND', '没有找到该本地演示字段');
    if (currentVersion(init) && currentVersion(init) !== field.rowVersion)
      return problem(
        409,
        'RESOURCE_VERSION_CONFLICT',
        '字段已更新，请重新读取',
      );
    const version = Number(field.definitionVersion) + 1;
    Object.assign(field, body, {
      definitionVersion: version,
      rowVersion: bump(field.rowVersion),
      optionsVersion: Array.isArray(body.options) ? version : null,
    });
    const displayName = state.fieldDisplayNames.find(
      (item) =>
        item.resourceType === field.resourceType &&
        item.groupKey === field.groupKey &&
        item.fieldKey === field.fieldKey,
    );
    if (displayName)
      displayName.defaultLabelZh = displayName.labelZh = field.labelZh;
    return write(field, 200, `"${field.rowVersion}"`);
  }
  if (pathname === '/quote-field-pool' && method === 'GET')
    return jsonResponse(
      state.quoteFieldPool,
      200,
      `"${state.quoteFieldPool.aggregateResourceVersion}"`,
    );
  if (pathname === '/quote-field-pool' && method === 'PUT') {
    if (
      currentVersion(init) &&
      currentVersion(init) !== state.quoteFieldPool.aggregateResourceVersion
    )
      return problem(
        409,
        'RESOURCE_VERSION_CONFLICT',
        '报价字段池已更新，请重新读取',
      );
    const bindings = Array.isArray(body.bindings)
      ? (body.bindings as Array<Record<string, unknown>>)
      : [];
    state.quoteFieldPool = {
      aggregateResourceVersion: String(
        Number(state.quoteFieldPool.aggregateResourceVersion) + 1,
      ),
      items: bindings.map((binding, index) => {
        const field = state.quoteFields.find(
          (item) => item.id === String(binding.definitionId),
        );
        return {
          id: `local-demo-binding-${index + 1}`,
          definitionId: String(binding.definitionId),
          definitionVersion: String(
            binding.definitionVersion ?? field?.definitionVersion ?? 1,
          ),
          fieldKey: String(binding.fieldKey ?? field?.fieldKey ?? ''),
          displayLabel: String(binding.displayLabel ?? field?.labelZh ?? ''),
          placement: String(
            binding.placement ?? 'quote-line',
          ) as QuoteFieldPoolList['items'][number]['placement'],
          snapshotPolicy: 'freeze-on-select',
          enabled: Boolean(binding.enabled),
          defaultVisible: Boolean(binding.defaultVisible),
          mandatory: false,
          sourceKind: 'manual',
          systemLocked: false,
          deletedAt: null,
          deletedBy: null,
          rowVersion: '1',
          sortOrder: Number(binding.sortOrder ?? index + 1),
        };
      }),
    };
    return write(
      state.quoteFieldPool,
      200,
      `"${state.quoteFieldPool.aggregateResourceVersion}"`,
    );
  }
  if (pathname === '/quote-output-options' && method === 'GET')
    return jsonResponse(
      state.outputOptions,
      200,
      `"${state.outputOptions.aggregateResourceVersion}"`,
    );
  if (pathname === '/quote-output-options' && method === 'PUT') {
    if (
      currentVersion(init) &&
      currentVersion(init) !== state.outputOptions.aggregateResourceVersion
    )
      return problem(
        409,
        'RESOURCE_VERSION_CONFLICT',
        '报价展示配置已更新，请重新读取',
      );
    state.outputOptions = {
      aggregateResourceVersion: String(
        Number(state.outputOptions.aggregateResourceVersion) + 1,
      ),
      fieldCombinations: Array.isArray(body.fieldCombinations)
        ? (body.fieldCombinations as QuoteOutputOptions['fieldCombinations'])
        : state.outputOptions.fieldCombinations,
      styles: Array.isArray(body.styles)
        ? (body.styles as QuoteOutputOptions['styles'])
        : state.outputOptions.styles,
    };
    return write(
      state.outputOptions,
      200,
      `"${state.outputOptions.aggregateResourceVersion}"`,
    );
  }
  if (pathname === '/quote-workspace-settings' && method === 'GET')
    return jsonResponse(
      state.workspaceSettings,
      200,
      `"${state.workspaceSettings.resourceVersion}"`,
    );
  if (pathname === '/quote-workspace-settings' && method === 'PUT') {
    if (
      currentVersion(init) &&
      currentVersion(init) !== state.workspaceSettings.resourceVersion
    )
      return problem(
        409,
        'RESOURCE_VERSION_CONFLICT',
        '工作台设置已更新，请重新读取',
      );
    state.workspaceSettings = {
      ...state.workspaceSettings,
      groupLabels: (body.groupLabels ??
        state.workspaceSettings
          .groupLabels) as LocalState['workspaceSettings']['groupLabels'],
      resourceVersion: String(
        Number(state.workspaceSettings.resourceVersion) + 1,
      ),
    };
    return write(
      state.workspaceSettings,
      200,
      `"${state.workspaceSettings.resourceVersion}"`,
    );
  }
  if (pathname === '/quote-workspace-container-edit-mode' && method === 'GET')
    return jsonResponse(
      state.containerEditMode,
      200,
      `"${state.containerEditMode.resourceVersion}"`,
    );
  if (pathname === '/quote-workspace-container-edit-mode' && method === 'PUT') {
    if (
      currentVersion(init) &&
      currentVersion(init) !== state.containerEditMode.resourceVersion
    )
      return problem(
        409,
        'RESOURCE_VERSION_CONFLICT',
        '容器编辑设置已更新，请重新读取',
      );
    state.containerEditMode = {
      enabled: Boolean(body.enabled),
      resourceVersion: String(
        Number(state.containerEditMode.resourceVersion) + 1,
      ),
    };
    return write(
      state.containerEditMode,
      200,
      `"${state.containerEditMode.resourceVersion}"`,
    );
  }
  if (pathname === '/quote-workspace-preferences' && method === 'GET')
    return jsonResponse(
      state.workspacePreferences,
      200,
      `"${state.workspacePreferences.resourceVersion}"`,
    );
  if (pathname === '/quote-workspace-preferences' && method === 'PUT') {
    if (
      currentVersion(init) &&
      currentVersion(init) !== state.workspacePreferences.resourceVersion
    )
      return problem(
        409,
        'RESOURCE_VERSION_CONFLICT',
        '个人工作台偏好已更新，请重新读取',
      );
    state.workspacePreferences = {
      selectedEquipmentPriceFields: Array.isArray(
        body.selectedEquipmentPriceFields,
      )
        ? body.selectedEquipmentPriceFields.map(String)
        : [],
      resourceVersion: String(
        Number(state.workspacePreferences.resourceVersion) + 1,
      ),
    };
    return write(
      state.workspacePreferences,
      200,
      `"${state.workspacePreferences.resourceVersion}"`,
    );
  }

  return undefined;
}

/**
 * Local-only adapters for admin/settings modules. Their editable data is saved
 * under the dedicated browser demo key; all other paths remain offline errors.
 */
export function createLocalModuleTransport(
  base: ActiveTransport,
  options: { catalog?: LocalCatalogSnapshotDtos } = {},
): ActiveTransport {
  const snapshot = options.catalog;
  const permissionTransport = createLocalPermissionTransport(base);
  const requestResponse = async (
    path: string,
    init: ActiveRequestInit = {},
  ) => {
    const method = (init.method ?? 'GET').toUpperCase();
    const url = new URL(path, window.location.origin);
    const handled = moduleResponse(url.pathname, url, method, init, snapshot);
    if (handled) return handled;
    return permissionTransport.requestResponse(path, init);
  };
  const request = async <T>(path: string, init: ActiveRequestInit = {}) => {
    const response = await requestResponse(path, init);
    if (!response.ok)
      throw new Error(`本地演示操作失败（HTTP ${response.status}）`);
    if (response.status === 204) return undefined as T;
    return (await response.json()) as T;
  };
  return { ...permissionTransport, requestResponse, request };
}
